/**
 * Runtime Amazon ad-tag assignment (`ecmtg.kueez.net/assign/ad`).
 *
 * Successful (and API-fallback) results are cached in sessionStorage under a
 * composite `kz_ecmata_*` key so reloads with the same assign inputs skip the
 * network call (yoto uses a 30m cookie; SDK keeps tab-scoped storage).
 */

import type { SdkContext } from '../types';
import type { AdAccountConfig } from '../../core/config/types';
import { sourceMediumPartsFromAccount } from '../../core/attribution/activeAdAccount';
import {
  AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS,
  AMAZON_BUCKET_MODE_SUBTAGS,
  type RuntimeAdTagAssignment,
} from './amazon';
import { debug } from '../../util/log';

const ASSIGN_AD_URL = 'https://ecmtg.kueez.net/assign/ad';
const ASSIGN_AD_TIMEOUT_MS = 8000;
const RUNTIME_ASSIGN_CACHE_PREFIX = 'kz_ecmata_';

type AssignAdResponse = {
  success?: boolean;
  tag?: string;
  parent_tag?: string;
  mode?: string;
  assignment_id?: number | string;
  fallback_reason?: string;
  fallback_status?: number;
};

type RuntimeAssignCacheParams = {
  esabi: string;
  mbid: string;
  ecmai: string;
  wd: string;
  w: string;
  utms: string;
  utmm: string;
  eatags?: string;
};

/* -------------------------------------------------------------------------- */
/* sessionStorage cache (yoto `ecmata_*` cookie parity, tab-scoped)             */
/* -------------------------------------------------------------------------- */

/** Composite key — same dimensions as yoto’s assign/ad cookie key. */
export function runtimeAdTagAssignmentCacheKey(params: RuntimeAssignCacheParams): string {
  const parts = ['esabi', 'mbid', 'ecmai', 'wd', 'w', 'utms', 'utmm', 'eatags'].map((key) => {
    const value = params[key as keyof RuntimeAssignCacheParams];
    return encodeURIComponent(value !== undefined && value !== null ? String(value) : '');
  });
  return `${RUNTIME_ASSIGN_CACHE_PREFIX}${parts.join('_')}`;
}

export function getCachedRuntimeAdTagAssignment(
  cacheKey: string,
): RuntimeAdTagAssignment | null {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RuntimeAdTagAssignment;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.fallback && !parsed.tag) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setCachedRuntimeAdTagAssignment(
  cacheKey: string,
  assignment: RuntimeAdTagAssignment,
): void {
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(assignment));
  } catch {
    /* ignore */
  }
}

/* -------------------------------------------------------------------------- */
/* Runtime assignment fetch                                                    */
/* -------------------------------------------------------------------------- */

function shouldUseRuntimeAdTagAssignment(adAccount: AdAccountConfig | null, mbid: string): boolean {
  if (typeof window === 'undefined') return false;

  const qs = new URLSearchParams(window.location.search);
  return qs.get('eatag') === '1' &&
    Boolean(adAccount?.adTagChannelBucket) &&
    Boolean(mbid);
}

export async function getRuntimeAdTagAssignment(
  ctx: SdkContext,
  adAccount: AdAccountConfig | null,
): Promise<RuntimeAdTagAssignment | null> {
  const mbid = ctx.getPageParams().mediaBuyingId;
  if (!shouldUseRuntimeAdTagAssignment(adAccount, mbid)) {
    debug('runtime ad-tag assign skipped', {
      eatag: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('eatag') : null,
      hasAdAccount: Boolean(adAccount),
      eatcb: adAccount?.adTagChannelBucket ?? null,
      mbid: mbid || null,
    });
    return null;
  }

  const config = ctx.getConfig();
  // Site-level ecom account id (yoto `window.ecmai`), not a field on the ad account.
  const ecomAccountId = config.ecom.ecomAccountId;
  const urlParams = new URLSearchParams(window.location.search);

  if (!mbid || !adAccount?.adTagChannelBucket || !ecomAccountId) {
    debug('runtime ad-tag assign missing inputs', {
      mbid: mbid || null,
      eatcb: adAccount?.adTagChannelBucket ?? null,
      ecomAccountId: ecomAccountId ?? null,
    });
    return { tag: null, fallback: true, fallbackReason: 'missing_inputs' };
  }

  const { source: utms, medium: utmm } = sourceMediumPartsFromAccount(adAccount);
  const eatags = urlParams.get('eatags') === '1' ? '1' : undefined;
  const cacheKey = runtimeAdTagAssignmentCacheKey({
    esabi: String(adAccount.adTagChannelBucket),
    mbid,
    ecmai: String(ecomAccountId),
    wd: config.website.domain,
    w: String(config.website.id),
    utms,
    utmm,
    ...(eatags ? { eatags } : {}),
  });

  const cached = getCachedRuntimeAdTagAssignment(cacheKey);
  if (cached) {
    debug('runtime ad-tag assign cache hit', { cacheKey, tag: cached.tag, fallback: cached.fallback });
    return cached;
  }

  const params = new URLSearchParams();
  params.set('mbid', mbid);
  params.set('esabi', String(adAccount.adTagChannelBucket));
  params.set('ecmai', String(ecomAccountId));
  params.set('surface', 'sdk');
  params.set('w', String(config.website.id));
  params.set('wd', config.website.domain);
  params.set('utms', utms);
  params.set('utmm', utmm);
  if (eatags) params.set('eatags', eatags);
  const ct = urlParams.get('ct');
  if (ct) params.set('ct', ct);

  const assignUrl = `${ASSIGN_AD_URL}?${params.toString()}`;
  debug('runtime ad-tag assign request', { url: assignUrl });

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ASSIGN_AD_TIMEOUT_MS);

  try {
    const response = await fetch(assignUrl, {
      method: 'POST',
      signal: controller.signal,
    });
    if (!response.ok) {
      debug('runtime ad-tag assign http error', { status: response.status });
      // Transient — do not cache (retry on next load), same as yoto.
      return { tag: null, fallback: true, fallbackReason: 'http_error', fallbackStatus: response.status };
    }

    const json = await response.json() as AssignAdResponse;
    if (
      !json.success ||
      !json.tag ||
      json.mode === AMAZON_BUCKET_MODE_DYNAMIC_SUBTAGS ||
      (json.mode === AMAZON_BUCKET_MODE_SUBTAGS && !json.parent_tag)
    ) {
      debug('runtime ad-tag assign invalid response', json);
      const fallback: RuntimeAdTagAssignment = {
        tag: null,
        fallback: true,
        fallbackReason: json.fallback_reason || 'invalid_response',
        fallbackStatus: json.fallback_status,
      };
      setCachedRuntimeAdTagAssignment(cacheKey, fallback);
      return fallback;
    }

    const assignment: RuntimeAdTagAssignment = {
      tag: json.tag,
      ...(json.parent_tag ? { parentTag: json.parent_tag } : {}),
      ...(json.mode ? { mode: json.mode } : {}),
      ...(json.assignment_id ? { assignmentId: String(json.assignment_id) } : {}),
      fallback: false,
    };
    setCachedRuntimeAdTagAssignment(cacheKey, assignment);
    debug('runtime ad-tag assign ok', {
      tag: assignment.tag,
      mode: assignment.mode,
      parentTag: assignment.parentTag,
      assignmentId: assignment.assignmentId,
    });
    return assignment;
  } catch (error) {
    debug('runtime ad-tag assign failed', error);
    // Transient — do not cache.
    return {
      tag: null,
      fallback: true,
      fallbackReason: error instanceof Error && error.name === 'AbortError' ? 'timeout' : 'request_error',
    };
  } finally {
    clearTimeout(timeout);
  }
}
